import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProfileTab from './ProfileTab';
import TodoTab from './TodoTab';
import CalendarTab from './CalendarTab';
import PlansTab from './PlansTab';
import ChatTab from './ChatTab';
import AdminTab from './AdminTab';
import GlobalOverviewTab from './GlobalOverviewTab';

const TABS = [
  { key: 'profile', emoji: '👤', path: 'profile' },
  { key: 'todo', emoji: '✅', path: 'todo' },
  { key: 'calendar', emoji: '📅', path: 'calendar' },
  { key: 'plans', emoji: '🎯', path: 'plans' },
  { key: 'chat', emoji: '💬', path: 'chat' },
];
const ADMIN_OVERVIEW_TAB = { key: 'globalOverview', emoji: '🌐', path: 'global-overview' };
const ADMIN_TAB = { key: 'admin', emoji: '🛠️', path: 'admin' };

function Dashboard({ user: initialUser, refreshUser }) {
  const { t } = useTranslation();
  const [user, setUserLocal] = useState(initialUser);
  const [profileScore, setProfileScore] = useState(initialUser?.profileScore ?? 0);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    setUserLocal(initialUser);
    setProfileScore(initialUser?.profileScore ?? 0);
  }, [initialUser]);

  useEffect(() => {
    const dense = initialUser?.appSettings?.denseUi === true;
    const reduce = initialUser?.appSettings?.reduceMotion === true;
    document.documentElement.toggleAttribute('data-haydi-dense', dense);
    document.documentElement.toggleAttribute('data-haydi-reduce-motion', reduce);
    return () => {
      document.documentElement.removeAttribute('data-haydi-dense');
      document.documentElement.removeAttribute('data-haydi-reduce-motion');
    };
  }, [initialUser?.appSettings?.denseUi, initialUser?.appSettings?.reduceMotion]);

  const tabs =
    user && user.role === 'admin'
      ? [...TABS, ADMIN_OVERVIEW_TAB, ADMIN_TAB]
      : TABS;
  const activeTab = tabs.find((tab) => location.pathname.endsWith(tab.path))?.key || 'profile';

  const handleTabClick = (tab) => {
    navigate(`/dashboard/${tab.path}`);
  };

  return (
    <div className="haydi-dashboard">
      <nav className="haydi-dashboard__nav" aria-label={t('dashboard')}>
        {tabs.map((tab) => (
          <button
            type="button"
            key={tab.key}
            className={`haydi-dashboard__tab${activeTab === tab.key ? ' haydi-dashboard__tab--active' : ''}`}
            onClick={() => handleTabClick(tab)}
          >
            <span className="haydi-dashboard__tab-emoji" aria-hidden>{tab.emoji}</span>
            {tab.key === 'admin'
              ? t('adminSettings')
              : tab.key === 'globalOverview'
                ? t('adminGlobalOverview')
                : t(tab.key)}
          </button>
        ))}
      </nav>

      <div className="haydi-dashboard__body">
        <Routes>
          <Route index element={<Navigate to="profile" replace />} />
          <Route path="profile" element={<ProfileTab user={user} profileScore={profileScore} refreshUser={refreshUser} />} />
          <Route path="todo" element={<TodoTab user={user} />} />
          <Route path="calendar" element={<CalendarTab user={user} />} />
          <Route path="plans" element={<PlansTab user={user} />} />
          <Route path="chat" element={<ChatTab user={user} />} />
          {user && user.role === 'admin' && (
            <Route path="global-overview" element={<GlobalOverviewTab />} />
          )}
          {user && user.role === 'admin' && (
            <Route path="admin" element={<AdminTab user={user} />} />
          )}
          <Route path="*" element={<Navigate to="profile" replace />} />
        </Routes>
      </div>
    </div>
  );
}

export default Dashboard;
